import type { AuthState, UserClaims } from '@okta/okta-auth-js';
import { useOktaAuth } from '@okta/okta-react';
import {
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { ErrorPage as ErrorScreen } from '~/components/ErrorPage';
import { bff, type UserResponse } from '~/lib/bff';
import type { User } from '~/modules/auth';
import { BrandContext } from '~/modules/branding';
import { LoadingScreen } from '~/modules/navigation/components/LoadingScreen';

import type { UserProfile } from '../types';
import { WmgSessionContext } from './WmgSessionContext';

const getUserProfile = (authState?: AuthState | null): UserProfile => {
  const claims = authState?.accessToken?.claims as Partial<
    UserClaims<UserProfile>
  >;

  const {
    businessUnit = '',
    jobFamilyGroup = '',
    team = '',
    label = '',
    title = '',
    countryCode = '',
    organization = '',
    company = '',
    userType = '',
    description = undefined,
    department = undefined,
  } = claims || {};

  return {
    businessUnit,
    jobFamilyGroup,
    team,
    label,
    title,
    countryCode,
    organization,
    company,
    userType,
    description,
    department,
  };
};

const getDefaultUser = ({
  user,
  authState,
}: {
  user?: Partial<User>;
  authState?: AuthState | null;
}) => {
  return {
    name: user?.name ?? '',
    email: user?.email ?? '',
    permissions: user?.permissions ?? {},
    isInternal: user?.isInternal ?? false,
    features: user?.features ?? [],
    profile: getUserProfile(authState),
  };
};

const WmgSessionProvider = ({ children }: PropsWithChildren) => {
  const { authState, oktaAuth } = useOktaAuth();
  const brand = useContext(BrandContext);
  const [error, setError] = useState<string | undefined>();
  const [user, setUser] = useState<UserResponse | undefined>();
  const [userInfo, setUserInfo] = useState<User>(getDefaultUser({}));

  const [authAttempted, setAuthAttempted] = useState(false);

  const oktaToken = authState?.accessToken?.accessToken;
  const oktaUserId = authState?.accessToken?.claims?.uid as string;

  const authenticateUser = useCallback(
    async (token?: string, userId?: string) => {
      if (!token || !userId) {
        return;
      }

      setError(undefined);

      try {
        const fetchedUser = await bff.getUser(token);
        const fetchedUserInfo = await bff.getUserInfo(token, userId);

        // We know that for a WMG user the fetchedUser comes back with empty
        // values (except userLocale). So get the user info from the Okta
        // token instead.
        if (brand.name === 'WMG') {
          const oktaUser = await oktaAuth.getUser();

          setUserInfo((prevState) => ({
            ...prevState,
            name: oktaUser.name ?? '',
            email: oktaUser.email ?? '',
            isInternal: true, // WMG users are always internal.
          }));
        } else {
          setUserInfo((prevState) => ({
            ...prevState,
            name: fetchedUser.name,
            email: fetchedUserInfo.email,
            permissions: fetchedUser.permissions,
            features: fetchedUserInfo.userFeatures,
            isInternal: fetchedUserInfo.isInternal,
          }));
        }

        // Make sure to set the user here AFTER making the call to getUser() above
        // for WMG1 users. If we don't, we'll get rid of the loading indicator and
        // the userInfo won't have all the data we need.
        setUser(fetchedUser);
      } catch (e) {
        setError((e as Error).message || 'Unexpected error');
      } finally {
        setAuthAttempted(true);
      }
    },
    [brand.name, oktaAuth],
  );

  useEffect(() => {
    authenticateUser(oktaToken, oktaUserId);
  }, [authenticateUser, oktaToken, oktaUserId]);

  const defaultUser = useMemo(() => {
    return getDefaultUser({ user: userInfo, authState });
  }, [authState, userInfo]);

  const sessionValue = useMemo(() => {
    return { user: defaultUser, token: oktaToken ?? '' };
  }, [defaultUser, oktaToken]);

  if (error) {
    return (
      <ErrorScreen
        message={error}
        onRetry={() => authenticateUser(oktaToken, oktaUserId)}
      />
    );
  }

  if (authState?.accessToken?.accessToken && !user && !authAttempted) {
    return <LoadingScreen />;
  }

  return (
    <WmgSessionContext.Provider value={sessionValue}>
      {children}
    </WmgSessionContext.Provider>
  );
};

WmgSessionProvider.displayName = 'WmgSessionProvider';

export { WmgSessionContext, WmgSessionProvider };
